import { Consult } from 'app/models/domain/consult'
import { ConsultDto } from 'app/models/generated'

/**
 * Sort items by startDate descending.
 * Exported for reuse across consult list hook and tests.
 */
export function sortByStartDateDesc<T extends { startDate?: string | null }>(
    items: T[]
): T[] {
    return [...items].sort((a, b) => {
        const dateA = a.startDate || ''
        const dateB = b.startDate || ''
        return dateB.localeCompare(dateA)
    })
}

/**
 * Map an array of ConsultDto items to unenriched Consult instances.
 * Exported for reuse across consult list hook and tests.
 */
export function buildUnenrichedConsults(items: ConsultDto[]): Consult[] {
    return items.map((item) => Consult.buildFromDto(item))
}

/**
 * Defense-in-depth owner scoping for the patient-detail consult list (ACA-126).
 *
 * The real access-control boundary is the server-side `@auth(allow: owner)`
 * rule on ConsultDto and its child models — a non-owner get is denied and the
 * owner-scoped list resolver never returns another clinician's rows. This
 * client filter is belt-and-suspenders: it guarantees the patient-detail view
 * only ever renders consults owned by the signed-in clinician, so a future
 * auth regression cannot silently leak another clinician's consult, note,
 * transcript, diarization or audio into the UI.
 *
 * When `ownerSub` is unknown (empty/undefined) the list is returned unchanged
 * rather than blanked, so a missing identity can never wipe a legitimate view.
 */
export function filterConsultsByOwner<T extends { owner?: string | null }>(
    items: T[],
    ownerSub: string | null | undefined
): T[] {
    if (!ownerSub) return items
    return items.filter((item) => item.owner === ownerSub)
}
